import * as Yup from "yup";

import {
  BoatType,
  typeWithMotor,
  typeWithMotorOrSail,
} from "Store/interfaces/products/checkout/riskProducts/BoatProduct";
import { getYear, subYears } from "date-fns";

import { TextManager } from "Components/TextContext";
import { ErrorPrefix } from "Constants/constants";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { BoatProductIntroFormValues } from "./boatProductIntroInterfaces";

export const validationSchema =
  (): Yup.ObjectSchema<BoatProductIntroFormValues> => {
    const { getText } = TextManager;

    return Yup.object({
      boatType: Yup.mixed<BoatType>().required(
        getText("boatform.validation.boattype"),
      ),

      brandAndModel: Yup.string().when("boatType", {
        is: typeWithMotorOrSail,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.brandandmodel"))
            .min(2, getText("boatform.validation.brandandmodel_tooshort"))
            .max(20, getText("boatform.validation.brandandmodel_toolong")),
      }),

      coverageAmount: Yup.number()
        .required(getText("boatform.validation.coverageamount"))
        .min(1000, getText("boatform.validation.coverageamount_toosmall"))
        .max(2000000, getText("boatform.validation.coverageamount_toolarge")),

      engineBrand: Yup.string().when("boatType", {
        is: BoatType.MOTOR_BOAT_OUTBOARDS,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.enginebrand"))
            .min(2, getText("boatform.validation.enginebrand_tooshort"))
            .max(20, getText("boatform.validation.enginebrand_toolong")),
      }),

      engineHorsePower: Yup.number().when("boatType", {
        is: typeWithMotor,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.enginehorsepower"))
            .min(1, getText("boatform.validation.enginehorsepower_toolow"))
            .max(600, getText("boatform.validation.enginehorsepower_toohigh")),
      }),

      engineTopSpeed: Yup.number().when("boatType", {
        is: typeWithMotor,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.enginetopspeed"))
            .min(1, getText("boatform.validation.enginetopspeed_toolow"))
            .max(50, getText("boatform.validation.enginetopspeed_toohigh")),
      }),

      lengthInFeet: Yup.number().when("boatType", {
        is: typeWithMotorOrSail,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.lengthinfeet"))
            .min(1, getText("boatform.validation.lengthinfeet_tooshort"))
            .max(48, getText("boatform.validation.lengthinfeet_toolong")),
      }),

      numberOfYearsAsOwner: Yup.string().required(
        getText("boatform.validation.numberofyearsasowner"),
      ),

      modelYear: Yup.number().when("boatType", {
        is: typeWithMotorOrSail,
        then: (schema) =>
          schema
            .required(getText("boatform.validation.modelyear_required"))
            .min(
              getYear(subYears(new Date(), 40)),
              getText("boatform.validation.modelyear"),
            )
            .max(getYear(new Date()), getText("boatform.validation.modelyear")),
      }),

      boatNotPristine: Yup.mixed<BooleanFormType>().when("boatType", {
        is: typeWithMotorOrSail,
        then: (schema) =>
          schema
            .oneOf([BooleanFormType.FALSE], ErrorPrefix.BREAKING)
            .required(getText("boatform.validation.boatnotpristine")),
      }),
    }).defined();
  };
